// Shared extractor for the sanitized, admin-safe error message off an
// ApiError thrown by the api client. The backend guarantees no upstream TikTok
// internals / account data leak into these messages; we never surface raw
// errors.
//
// Ops should see a DIAGNOSIS + a NEXT STEP, not a bare machine token like
// "conflict". So we, in order: (1) map a recognised structured refusal `reason`
// to a full sentence, (2) surface a genuine server-authored human `message`,
// (3) keep a DESCRIPTIVE server `error` string verbatim (e.g. "upstream
// error"), (4) fall back to a friendly per-status sentence, and only then (5)
// the raw client message / generic fallback. Structured refusals that have
// their OWN dedicated UI (undelivered_qty force-share, the BC-link 422 panels,
// the allowlist unbound_client toast) never reach here — they are handled at
// the call site — so mapping their reasons here is a harmless safety net.

import type { ApiError } from "@/api/client";

// A recognised structured refusal reason -> an operator-facing sentence. These
// are the reason codes the admin backend returns in the error body's `reason`.
const REASON_MESSAGES: Record<string, string> = {
  already_registered:
    "This account is already registered to a client — unlink it first.",
  unbound_client: "Assign this request to a client first.",
  not_verified: "That balance isn't verified yet.",
};

// Bare machine codes the backend puts in the body's `error` field. On their own
// these are not user-facing, so the status map below explains them better. A
// DESCRIPTIVE `error` string (anything not in this set) is kept verbatim.
const BARE_ERROR_CODES = new Set([
  "bad request",
  "unauthorized",
  "forbidden",
  "not found",
  "conflict",
  "unprocessable",
  "unprocessable entity",
  "too many requests",
  "internal server error",
  "bad gateway",
  "service unavailable",
  "gateway timeout",
]);

// Friendly fallback per HTTP status when there is no more specific reason or
// server message to show.
const STATUS_MESSAGES: Record<number, string> = {
  400: "That request was rejected — check the fields and try again.",
  401: "Your session has expired — sign in again.",
  403: "You do not have permission to do that.",
  404: "That item could not be found — it may have been removed.",
  409: "That action conflicts with the current state — refresh and try again.",
  422: "The backend rejected that as invalid.",
  429: "Too many requests — wait a moment and retry.",
  500: "The server hit an unexpected error — try again shortly.",
  502: "The upstream service is unavailable — try again shortly.",
  503: "The service is temporarily unavailable — try again shortly.",
};

export function errMessage(
  err: unknown,
  fallback = "Something went wrong",
): string {
  const e = err as ApiError | undefined;
  if (!e || typeof e !== "object") return fallback;

  const body = e.body as
    | { message?: unknown; error?: unknown; reason?: unknown }
    | undefined;

  // 1. A recognised structured refusal reason -> diagnosis + next step.
  if (typeof body?.reason === "string" && REASON_MESSAGES[body.reason]) {
    return REASON_MESSAGES[body.reason];
  }

  // 2. A genuine server-authored human message.
  if (typeof body?.message === "string" && body.message.trim()) {
    return body.message.trim();
  }

  // 3. A descriptive server `error` string, but NOT a bare machine code
  //    (a bare "conflict" is explained better by the status map below).
  const errStr = typeof body?.error === "string" ? body.error.trim() : "";
  if (errStr && !BARE_ERROR_CODES.has(errStr.toLowerCase())) {
    return errStr;
  }

  // 4. Friendly per-status fallback.
  const status = typeof e.status === "number" ? e.status : 0;
  if (STATUS_MESSAGES[status]) return STATUS_MESSAGES[status];

  // 5. Whatever the client already extracted, else the generic fallback.
  return (typeof e.message === "string" && e.message.trim()) || fallback;
}
